<template>
  <div class="topics-management">
    <!-- Header Section -->
    <div class="header-section">
      <h1 class="page-title">Competency Management</h1>
      <p class="page-subtitle">
        Define and manage competency and weightage distribution
      </p>
    </div>

    <!-- Main Content -->
    <div class="main-content">
      <!-- Subject List Header -->
      <div class="subject-header">
        <div class="header-left">
          <h2 class="section-title">Competency List</h2>
          <span class="subject-count">{{ totalSubjects }} competency configured</span>
        </div>
        <div class="header-right">
          <!-- Filter & Actions Section -->
          <div class="filter-actions-container">
            <button class="filter-btn" @click="toggleFilterPanel" :class="{ active: showFilterPanel }">
              <!-- <i class="bi bi-funnel"></i> -->
              <i class="bi bi-gear"></i>
              Action
              <span v-if="selectedForFilter.length > 0" class="filter-count">{{
                selectedForFilter.length
              }}</span>
            </button>
            <div v-if="showFilterPanel" class="action-dropdown">
              <ul>
                <li
                  @click="removeItemsFilter = false; applyWeightsFilter = true; movesFilter = false; showFilterPanel = false;">
                  <span class="action-dropdown-icon"> <i class="bi bi-sliders2-vertical"></i></span>
                  <span>Apply Weights</span>
                </li>
                <li
                  @click="removeItemsFilter = true; applyWeightsFilter = false; movesFilter = false; showFilterPanel = false">
                  <span class="action-dropdown-icon"><i class="bi bi-trash"></i></span>
                  <span>Remove</span>
                </li>
                <li
                  @click="removeItemsFilter = false; applyWeightsFilter = false; movesFilter = true; showFilterPanel = false">
                  <span class="action-dropdown-icon"> <i class="bi bi-arrows-move"></i></span>
                  <span>Move</span>
                </li>
              </ul>
            </div>
          </div>

          <div class="search-container">
            <i class="bi bi-search search-icon"></i>
            <input type="text" class="search-input" placeholder="Search competency..." v-model="searchQuery" />
          </div>
          <button class="create-btn" @click="openAddSubjectModal">
            Add Competency
          </button>
        </div>
      </div>

      <!-- Filter Panel -->
      <div v-if="removeItemsFilter || applyWeightsFilter || movesFilter" class="filter-panel">
        <div class="filter-header">
          <h3>Bulk Actions</h3>
          <button class="close-filter-btn" @click="closeFilterPanel">
            <i class="bi bi-x"></i>
          </button>
        </div>

        <div class="filter-content">
          <div class="filter-section">
            <h4>Selected Competencies ({{ selectedForFilter.length }})</h4>
            <div class="selected-subjects-list">
              <div v-for="subjectId in selectedForFilter" :key="subjectId" class="selected-subject-item">
                <span>{{ getSubjectName(subjectId) }}</span>
                <button @click="removeFromFilter(subjectId)" class="remove-btn">
                  <i class="bi bi-x"></i>
                </button>
              </div>
              <div v-if="selectedForFilter.length === 0" class="no-selection">
                No competencies selected. Click checkboxes in the table to
                select competencies.
              </div>
            </div>
          </div>

          <div class="filter-actions">
            <div class="action-group" v-if="applyWeightsFilter">
              <label>Apply Equal Weightage:</label>
              <div class="weight-input-group">
                <input type="number" v-model.number="bulkWeight" min="1" max="100" placeholder="Weight %"
                  class="weight-input" />
                <button @click="applyBulkWeight" :disabled="!canApplyBulkWeight" class="apply-btn">
                  Apply
                </button>
              </div>
            </div>

            <div class="action-group" v-if="removeItemsFilter">
              <label>Remove Items:</label>
              <div class="weight-input-group">
                <button @click="applyBulkRemovesItems" :disabled="!canApplyBulkRemove" class="apply-btn">
                  Apply
                </button>
              </div>
            </div>

            <div class="action-group" v-if="movesFilter">
              <label>Move</label>
              <div class="parent-select-group">
                <select v-model="newParentId" class="parent-select">
                  <option value="">-- Root Level --</option>
                  <option v-for="subject in availableParentsForMove" :key="subject.id" :value="subject.id">
                    {{ subject.displayName || subject.name }} (Level
                    {{ subject.level }})
                  </option>
                </select>
                <button @click="moveToParent" :disabled="!canMoveToParent" class="apply-btn">
                  Move
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Weight Status Alerts -->
      <div v-if="weightValidationErrors.length > 0" class="weight-alert">
        <i class="bi bi-exclamation-triangle"></i>
        <div>
          <div v-for="error in weightValidationErrors" :key="error" class="weight-error-item">
            {{ error }}
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="table-container">
        <table class="subject-table">
          <thead>
            <tr>
              <th class="checkbox-col" style="text-align: center"
                v-if="removeItemsFilter || applyWeightsFilter || movesFilter">
                <input type="checkbox" :checked="allTableItemsSelected" @change="toggleAllTableSelection"
                  class="table-checkbox" />
              </th>
              <th class="subject-col">Competency Name</th>
              <th class="parent-col">Parent Competency</th>
              <th class="level-col">Level</th>
              <th class="weight-col">Weight (%)</th>
              <!-- <th class="descriptor-col">Descriptor Status</th> -->
              <th class="actions-col">Actions</th>
            </tr>
          </thead>
          <tbody ref="tableBody">
            <tr v-for="(subject, index) in filteredSubjects"
              :key="`drag-${subject.id}-${forceUpdate}-${index}-${subject.name}`" class="table-row" :class="{
                [`level-${Math.min(subject.level, 6)}`]: true,
                unconfigured: subject.weight === 0,
                'weight-invalid': isWeightInvalid(subject),
                dragging: draggedItem && draggedItem.id === subject.id,
                'drop-target': dropTarget && dropTarget.id === subject.id,
              }" :data-level="subject.level" :data-subject-id="subject.id" draggable="true"
              @dragstart="onDragStart($event, subject, index)" @dragover="onDragOver($event, subject)"
              @dragleave="onDragLeave" @drop="onDrop($event, subject)" @dragend="onDragEnd">
              <td class="checkbox-col" v-if="removeItemsFilter || applyWeightsFilter || movesFilter">
                <input type="checkbox" :checked="selectedForFilter.includes(subject.id)"
                  @change="toggleFilterSelection(subject.id)" class="table-checkbox" />
              </td>
              <td class="subject-col">
                <div class="subject-name-container">
                  <!-- Tree Structure Lines -->
                  <div class="tree-structure" v-if="subject.level > 1">
                    <div class="tree-vertical-line"></div>
                    <div class="tree-horizontal-line"></div>
                  </div>

                  <!-- Drag Handle -->
                  <div class="drag-handle" title="Drag to reorder">
                    <i class="bi bi-grip-vertical"></i>
                  </div>

                  <!-- Level icon -->
                  <div class="level-icon" :class="`level-${Math.min(subject.level, 6)}-icon`">
                    <i v-if="subject.level === 1" class="bi bi-house-fill"></i>
                    <i v-else-if="subject.level === 2" class="bi bi-folder-fill"></i>
                    <i v-else-if="subject.level === 3" class="bi bi-file-earmark-fill"></i>
                    <i v-else-if="subject.level === 4" class="bi bi-files"></i>
                    <i v-else-if="subject.level === 5" class="bi bi-file-text"></i>
                    <i v-else class="bi bi-dot"></i>
                  </div>

                  <!-- Subject Name -->
                  <span class="subject-name" :class="{
                    [`level-${Math.min(subject.level, 6)}-text`]: true,
                  }">
                    {{ subject.displayName || subject.name }}
                  </span>

                  <!-- Children count indicator -->
                  <span v-if="getChildrenCount(subject.id) > 0" class="children-count">
                    ({{ getChildrenCount(subject.id) }}
                    {{
                      getChildrenCount(subject.id) === 1 ? "child" : "children"
                    }})
                  </span>

                  <!-- Weight status for parents -->
                  <span v-if="getChildrenCount(subject.id) > 0" class="parent-weight-status"
                    :class="getParentWeightStatusClass(subject.id)">
                    {{ getChildrenTotalWeight(subject.id) }}%
                  </span>
                </div>
              </td>
              <td class="parent-col">
                <span class="parent-text">{{
                  getParentName(subject.parentId) || "-"
                }}</span>
              </td>
              <td class="level-col">
                <span class="level-badge" :class="`level-${Math.min(subject.level, 6)}`">
                  {{ subject.level }}
                </span>
              </td>
              <td class="weight-col">
                <span class="weight-badge" :class="getWeightBadgeClass(subject.weight)">
                  {{ subject.weight }}%
                </span>
              </td>

              <!-- <td class="descriptor-col">
                <span class="descriptor-badge" v-if="subject.descriptorStatus">configured
                </span>
                <span class="descriptor-badge" v-else-if="!subject.descriptorStatus">-
                </span>
              </td> -->

              <td class="actions-col">
                <div class="actions-container">
                  <button class="action-btn edit-btn" @click="editSubject(index)" title="Edit">
                    <i class="bi bi-pencil"></i>
                  </button>
                  <button class="action-btn delete-btn" @click="deleteSubject(index)" title="Remove">
                    <i class="bi bi-trash"></i>
                  </button>
                  <button class="action-btn add-child-btn" @click="addChildSubject(subject)"
                    title="Add Child Competency">
                    <i class="bi bi-plus"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Footer -->
      <div class="table-footer">
        <div class="footer-left">
          <span class="weight-status" :class="getWeightStatusClass()">
            <i class="bi bi-check-circle-fill"></i>
            Total Level 1 Weight: {{ level1Weight }}% / 100%
          </span>
        </div>
      </div>

      <!-- Configure Button -->
      <div class="configure-section">
        <button class="configure-btn" @click="configureSubject" :disabled="!canProceed">
          {{
            canProceed
              ? "Configure Competency"
              : `Complete All Weight Requirements`
          }}
        </button>
      </div>
    </div>

    <!-- Add Subject Modal -->
    <div v-if="showAddSubjectModal" class="modal-overlay" @click="closeAddSubjectModal">
      <div class="add-modal-content" @click.stop>
        <div class="modal-header">
          <h3>
            {{
              pendingParent
                ? `Add Child Competencies to "${pendingParent.name}"`
                : "Add Competency"
            }}
          </h3>
          <button class="close-btn" @click="closeAddSubjectModal">
            <i class="bi bi-x"></i>
          </button>
        </div>

        <div class="modal-body">
          <!-- Search Bar -->
          <!-- <div class="search-section">
            <div class="search-container-full">
              <i class="bi bi-search search-icon"></i>
              <input type="text" class="search-input-full" placeholder="Search Competency..."
                v-model="subjectSearchQuery" />
            </div>
          </div> -->

          <div class="search-section">
            <div class="search-container-full">
              <i class="bi bi-search search-icon"></i>
              <input type="text" class="search-input-full" placeholder="Search Competency..."
                v-model="subjectSearchQuery" @input="onSearchInput" @keydown.enter="onSearchEnter" />
            </div>
          </div>


          <!-- Action Buttons -->
          <div class="action-buttons">
            <button class="create-subject-btn" @click="openCreateNewModal">
              <i class="bi bi-plus-circle"></i>
              Create New Competency
            </button>
            <button class="ai-generate-btn" @click="generateWithAI">
              <i class="bi bi-magic"></i>
              Generate with AI
            </button>
          </div>

          <!-- Subject Cards Grid -->
          <div class="subject-cards-grid">
            <div v-for="subject in paginatedSubjects" :key="subject.id" class="subject-card"
              :class="{ selected: isSelected(subject.id) }" @click="toggleSelection(subject.id)">
              <div class="card-content">
                <span class="card-name">{{ subject.name }}</span>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="filteredDatabaseSubjects.length === 0" class="empty-state">
            <div class="empty-icon">
              <i class="bi bi-search"></i>
            </div>
            <!-- <h4>No Competency found</h4> -->
            <!-- <p>Try adjusting your search or create a new competency</p> -->

            <!-- Show "Searching..." if search is active -->
            <template v-if="subjectSearchQuery.trim().length > 0">
              <template v-if="!searchFlag && filteredDatabaseSubjects.length === 0">
                <h4>Searching for "{{ subjectSearchQuery }}"...</h4>
                <p>Please wait while we fetch results.</p>
              </template>

              <template v-if="searchFlag && filteredDatabaseSubjects.length === 0">
                <h4>No results for "{{ subjectSearchQuery }}"</h4>
                <p>Try a different search term.</p>
              </template>

            </template>

            <!-- Default empty state when there's no search query -->
            <template v-else>
              <h4>No Competency found</h4>
              <p>Try adjusting your search or create a new competency</p>
            </template>


          </div>

          <!-- Pagination -->
          <div v-if="filteredDatabaseSubjects.length > 0" class="pagination-section">
            <div class="pagination-info">
              Showing {{ startIndex + 1 }}-{{
                endIndex
              }}
              of {{ totalCompetencies }} competencies
            </div>
            <div class="pagination-controls">
              <button class="pagination-btn" @click="previousPage" :disabled="currentPage === 1">
                <i class="bi bi-chevron-left"></i>
                Previous
              </button>
              <span class="page-indicator">{{ currentPage }} of {{ totalPages }}</span>
              <button class="pagination-btn" @click="nextPage" :disabled="currentPage === totalPages">
                Next
                <i class="bi bi-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer">
          <div class="footer-left">
            <button class="select-all-btn" @click="toggleSelectAll">
              <i class="bi bi-check2-all"></i>
              {{ allSelected ? "Deselect All" : "Select All" }}
            </button>
            <span class="selection-count">{{ selectedSubjects.length }} selected</span>
          </div>
          <div class="footer-right">
            <button class="cancel-btn" @click="closeAddSubjectModal">
              Cancel
            </button>
            <button class="add-selected-btn" @click="addSelectedSubjects" :disabled="selectedSubjects.length === 0">
              Add Selected ({{ selectedSubjects.length }})
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Create New Subject Modal -->
    <div v-if="showCreateNewModal" class="modal-overlay" @click="closeCreateNewModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Create New Competency</h3>
          <button class="close-btn" @click="closeCreateNewModal">
            <i class="bi bi-x"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Competency Name *</label>
            <input type="text" class="modal-input" v-model="createNewForm.name" placeholder="Enter Competency name"
              maxlength="100" />
            <div v-if="createNewForm.name && createNewForm.name.length < 3" class="field-error">
              Name must be at least 3 characters
            </div>
          </div>

          <div class="form-group">
            <label>Description</label>
            <textarea class="modal-input modal-textarea" v-model="createNewForm.description"
              placeholder="Enter description" rows="4" maxlength="500"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="closeCreateNewModal">
            Cancel
          </button>
          <button class="save-btn" @click="createNewSubject" :disabled="!canCreateNew">
            Create Competency
          </button>
        </div>
      </div>
    </div>

    <!-- Edit Competency Modal -->
    <div v-if="showEditModal" class="modal-overlay" @click="closeEditModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <div class="edit-header-content">
            <h3>Edit Competency</h3>
            <div class="simple-info">
              <div class="simple-info-line">
                <span class="simple-label">ID:</span>
                <span class="simple-value">{{
                  editingSubject?.subjectId || "N/A"
                }}</span>
                <span class="simple-separator">•</span>
                <span class="simple-label">Level:</span>
                <span class="simple-value">{{
                  editingSubject?.level || "N/A"
                }}</span>
              </div>
              <div class="simple-info-line">
                <span class="simple-name">{{
                  editingSubject?.name || "N/A"
                }}</span>
              </div>
            </div>
          </div>
          <button class="close-btn" @click="closeEditModal">
            <i class="bi bi-x"></i>
          </button>
        </div>
        <div class="modal-body">
          <!-- Parent Competency (Disabled if exists) -->
          <div v-if="editingCompetency?.parentId" class="form-group">
            <span class="section-subheading">Parent Competency</span>
            <input type="text" class="modal-input disabled-input" :value="getParentName(editingSubject?.parentId) || ''"
              readonly disabled />
          </div>

          <!-- Display Name (Editable) -->
          <div class="form-group">
            <span class="section-subheading">Display Name *</span>
            <input type="text" class="modal-input" v-model="editForm.displayName" placeholder="Enter display name"
              maxlength="100" />
            <div v-if="editForm.displayName && editForm.displayName.length < 3" class="field-error">
              Display name must be at least 3 characters
            </div>
          </div>

          <!-- Weight -->
          <div class="form-group">
            <span class="section-subheading">Weight (%) *</span>
            <input type="number" class="modal-input" v-model.number="editForm.weight"
              placeholder="Enter weight percentage" min="1" max="100" />
            <div class="weight-info">
              <span class="current-total">Current siblings total: {{ getRelevantTotalWeight() }}%</span>
            </div>
            <div v-if="
              editForm.weight &&
              (editForm.weight < 1 || editForm.weight > 100)
            " class="field-error">
              Weight must be between 1 and 100
            </div>
            <div v-if="editForm.weight && willExceedTotalWeight" class="field-error">
              This weight would make total exceed 100%
            </div>
          </div>

          <!-- In Create New Modal -->
          <!-- <div class="form-group" v-if="shouldShowDescriptorOption">
            <span class="section-subheading">Descriptor Status*</span>
            <div class="descripter-status-div">
              <span class="descripter-status-title">
                Would you like to allow descriptor at this level?
              </span>
              <span>
                <input class="descripter-status-input form-check-input" type="checkbox" id="createDescriptorStatus"
                  v-model="editForm.descriptorStatus" />
              </span>
            </div>
          </div> -->
          <!-- Description -->
          <div class="form-group">
            <span class="section-subheading">Description</span>
            <textarea class="modal-input modal-textarea" v-model="editForm.description" placeholder="Enter description"
              rows="4" maxlength="500"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="closeEditModal">Cancel</button>
          <button class="save-btn" @click="saveEditedSubject" :disabled="!canSaveEdit">
            Update Competency
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
export default {
  name: "TopicsManagement",
  props: {
    formData: {
      type: Object,
      default: () => ({ topics: [] }),
    },
  },
  emits: ["update-data", "step-completed"],
  data() {
    return {
      localFormData: {
        topics: [],
      },
      internalUpdate: false,
      searchQuery: "",
      showAddSubjectModal: false,
      showCreateNewModal: false,
      showEditModal: false,
      editingIndex: null,
      editingSubject: null,
      forceUpdate: 0,

      // Filter Panel
      showFilterPanel: false,
      removeItemsFilter: false,
      applyWeightsFilter: false,
      movesFilter: false,
      selectedForFilter: [],
      bulkWeight: null,
      newParentId: "",

      // Drag and Drop
      draggedItem: null,
      dropTarget: null,

      // Add Subject Modal Data
      subjectSearchQuery: "",
      lastInputTime: null,
      checkIntervalId: null,
      hasFetched: false,
      selectedSubjects: [],
      currentPage: 1,
      itemsPerPage: 10,
      pendingParent: null,
      totalPages: null,
      totalCompetencies: null,
      searchFlag: false,

      // databaseSubjects: [
      //   {
      //     id: 1,
      //     name: "Communication",
      //     description:
      //       "Skill in conveying information effectively through verbal, non-verbal, and written forms.",
      //     displaName: "Communication",
      //   },
      // ],

      databaseSubjects: [],
      createNewForm: {
        name: "",
        description: "",
      },

      editForm: {
        displayName: "",
        weight: null,
        description: "",
        descriptorStatus: false,
      },
    };
  },
  computed: {
    filteredSubjects() {
      let subjects = this.localFormData.topics.map((comp) => ({ ...comp }));

      if (this.searchQuery && this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim();
        subjects = subjects.filter(
          (comp) =>
            (comp.name && comp.name.toLowerCase().includes(query)) ||
            (comp.displayName &&
              comp.displayName.toLowerCase().includes(query)) ||
            (comp.description && comp.description.toLowerCase().includes(query))
        );
      }

      return this.sortHierarchically(subjects);
    },

    filteredDatabaseSubjects() {
      if (!this.subjectSearchQuery.trim()) {
        const topicIds = this.localFormData.topics.map((topic) => topic.id);
        const filterdata = this.databaseSubjects.filter(
          (subdata) => !topicIds.includes(subdata.id)
        );
        return filterdata;
      }
      const query = this.subjectSearchQuery.toLowerCase().trim();
      if (query.length > 0 && this.searchFlag) {
        return this.databaseSubjects.filter((comp) =>
          comp.name.toLowerCase().includes(query)
        );
      } else {
        return []
      }
    },

    // totalPages() {
    //   return Math.ceil(
    //     this.filteredDatabaseSubjects.length / this.itemsPerPage
    //   );
    // },

    startIndex() {
      return (this.currentPage - 1) * this.itemsPerPage;
      // return 0;
    },

    endIndex() {
      return Math.min(this.totalCompetencies, this.startIndex + this.itemsPerPage);
      // return this.itemsPerPage;
    },

    paginatedSubjects() {
      return this.filteredDatabaseSubjects.slice(
        // this.startIndex,
        // this.endIndex
        0, this.itemsPerPage
      );
    },

    allSelected() {
      return (
        this.paginatedSubjects.length > 0 &&
        this.paginatedSubjects.every((comp) => this.isSelected(comp.id))
      );
    },

    totalSubjects() {
      return this.localFormData.topics.length;
    },

    level1Weight() {
      return this.localFormData.topics
        .filter((comp) => comp.level === 1)
        .reduce((sum, comp) => sum + (comp.weight || 0), 0);
    },

    weightValidationErrors() {
      const errors = [];

      if (this.level1Weight !== 100) {
        errors.push(
          `Level 1 competencies must total 100% (Current: ${this.level1Weight}%)`
        );
      }

      const parents = this.localFormData.topics.filter(
        (comp) => this.getChildrenCount(comp.id) > 0
      );

      parents.forEach((parent) => {
        const childrenTotal = this.getChildrenTotalWeight(parent.id);
        if (childrenTotal !== 100) {
          errors.push(
            `"${parent.displayName || parent.name
            }" sub-competencies must total 100% (Current: ${childrenTotal}%)`
          );
        }
      });

      return errors;
    },

    canProceed() {
      return this.weightValidationErrors.length === 0;
    },

    // Filter computeds
    canApplyBulkWeight() {
      return (
        this.selectedForFilter.length > 0 &&
        this.bulkWeight &&
        this.bulkWeight >= 1 &&
        this.bulkWeight <= 100
      );
    },

    canApplyBulkRemove() {
      return (
        this.selectedForFilter.length > 0
      );
    },


    canMoveToParent() {
      return this.selectedForFilter.length > 0;
    },

    availableParentsForMove() {
      return this.localFormData.topics.filter(
        (subject) =>
          !this.selectedForFilter.includes(subject.id) &&
          !this.selectedForFilter.some((selectedId) =>
            this.isChildOf(subject.id, selectedId)
          )
      );
    },

    allTableItemsSelected() {
      return (
        this.filteredSubjects.length > 0 &&
        this.filteredSubjects.every((subject) =>
          this.selectedForFilter.includes(subject.id)
        )
      );
    },

    availableParents() {
      if (this.editingIndex !== null) {
        const currentId = this.localFormData.topics[this.editingIndex]?.id;
        return this.localFormData.topics.filter(
          (comp) => comp.id !== currentId && !this.isChildOf(comp.id, currentId)
        );
      }
      return this.localFormData.topics;
    },

    willExceedTotalWeight() {
      if (!this.editForm.weight) return false;

      const parentId = this.editingSubject?.parentId;
      const siblings = this.localFormData.topics.filter(
        (comp) => comp.parentId === parentId
      );
      let currentTotal = siblings.reduce(
        (sum, comp) => sum + (comp.weight || 0),
        0
      );

      if (this.editingIndex !== null) {
        const editingComp = this.localFormData.topics[this.editingIndex];
        if (editingComp.parentId === parentId) {
          currentTotal -= editingComp.weight;
        }
      }

      return currentTotal + this.editForm.weight > 100;
    },

    canCreateNew() {
      return this.createNewForm.name && this.createNewForm.name.length >= 3;
    },

    canSaveEdit() {
      return (
        this.editForm.displayName &&
        this.editForm.displayName.length >= 3 &&
        this.editForm.weight &&
        this.editForm.weight >= 1 &&
        this.editForm.weight <= 100 &&
        !this.willExceedTotalWeight
      );
    },

    shouldShowDescriptorOption() {
      if (!this.editingSubject) return true;

      // Check if any ancestor has descriptor status enabled
      if (this.hasAncestorWithDescriptor(this.editingSubject.id)) {
        return false;
      }

      // Check if any descendant has descriptor status enabled
      if (this.hasDescendantWithDescriptor(this.editingSubject.id)) {
        return false;
      }

      return true;
    },
  },

  watch: {
    formData: {
      handler(newFormData) {
        if (newFormData && JSON.stringify(this.localFormData.topics) !== JSON.stringify(newFormData.topics)) {
          this.internalUpdate = true;
          this.localFormData.topics = [...newFormData.topics];
        }
      },
      deep: true,
      immediate: true,
    },
    localFormData: {
      handler(newData) {
        if (this.internalUpdate) {
          this.internalUpdate = false;
        } else {
          this.$emit("update-data", {
            stepKey: "topicsManagement",
            data: newData,
          });
        }
      },
      deep: true,
    }
  },


  methods: {
    ...mapActions([
      "GetCompetencyData",
      "CreateNewCompetency"]),


    onSearchInput() {
      this.searchFlag = false;
    },

    async getCompetencyData() {
      this.databaseSubjects = []
      var response = await this.GetCompetencyData({
        pageNumber: this.currentPage,
        pageSize: this.itemsPerPage,
        searchStr: this.subjectSearchQuery || "",
      });

      const catResponse = response?.object?.data
      if (catResponse) {
        this.totalPages = catResponse?.totalPages
        this.currentPage = catResponse.currentPage
        this.totalCompetencies = catResponse.totalItems
        return catResponse?.competencies.map(item => {
          return {
            id: item.competencyId,
            name: item.competencyDisplayName,
            description: item.description,
            displaName: item.competencyDisplayName,

          }
        })
      }
      return []
    },
    // Utility methods
    generateSubjectId() {
      return Math.floor(100000 + Math.random() * 900000).toString();
    },

    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.showNotification("Competency ID copied to clipboard!", "success");
      });
    },

    async onSearchEnter() {
      this.searchFlag = false
      this.currentPage = 1;
      this.databaseSubjects = await this.getCompetencyData()
      this.searchFlag = true
    },

    // Filter methods
    toggleFilterPanel() {
      this.showFilterPanel = !this.showFilterPanel;
      this.removeItemsFilter = false;
      this.applyWeightsFilter = false;
      this.movesFilter = false;
    },

    closeFilterPanel() {
      this.showFilterPanel = false;
      this.removeItemsFilter = false;
      this.applyWeightsFilter = false;
      this.movesFilter = false;
    },

    toggleFilterSelection(subjectId) {
      const index = this.selectedForFilter.indexOf(subjectId);
      if (index > -1) {
        this.selectedForFilter.splice(index, 1);
      } else {
        this.selectedForFilter.push(subjectId);
      }
    },

    toggleAllTableSelection() {
      if (this.allTableItemsSelected) {
        this.selectedForFilter = [];
      } else {
        this.selectedForFilter = [...this.filteredSubjects.map((s) => s.id)];
      }
    },

    removeFromFilter(subjectId) {
      const index = this.selectedForFilter.indexOf(subjectId);
      if (index > -1) {
        this.selectedForFilter.splice(index, 1);
      }
    },

    getSubjectName(subjectId) {
      const subject = this.localFormData.topics.find((s) => s.id === subjectId);
      return subject ? subject.displayName || subject.name : "Unknown";
    },

    applyBulkWeight() {
      if (!this.canApplyBulkWeight) return;

      const newTopics = [...this.localFormData.topics];
      this.selectedForFilter.forEach((subjectId) => {
        const index = newTopics.findIndex((s) => s.id === subjectId);
        if (index > -1) {
          newTopics[index] = { ...newTopics[index], weight: this.bulkWeight };
        }
      });

      this.localFormData.topics = newTopics;
      this.forceUpdate++;
      this.showNotification(
        `Applied ${this.bulkWeight}% weight to ${this.selectedForFilter.length} Competency`,
        "success"
      );
    },

    applyBulkRemovesItems() {
      if (!this.canApplyBulkRemove) return;
      const newTopics = [...this.localFormData.topics];
      this.selectedForFilter.forEach((subjectId) => {
        const filterCompData = newTopics.find(s => s.id === subjectId);
        if (filterCompData) {
          this.deleteComp(filterCompData)
        }
      });
      this.showNotification(
        `Delete total ${this.selectedForFilter.length} Competencies`,
        "success"
      );
      this.selectedForFilter = []
    },

    moveToParent() {
      if (!this.canMoveToParent) return;

      const newTopics = [...this.localFormData.topics];
      const newParent = this.newParentId
        ? newTopics.find((s) => s.id === this.newParentId)
        : null;
      const newLevel = newParent ? newParent.level + 1 : 1;
      const updateChildLevels = (parentId, levelDiff) => {
        newTopics.forEach((item) => {
          if (item.parentId === parentId) {
            item.level += levelDiff;
            updateChildLevels(item.id, levelDiff);
          }
        });
      };

      this.selectedForFilter.forEach((subjectId) => {
        const index = newTopics.findIndex((s) => s.id === subjectId);
        if (index > -1) {
          const currentItem = newTopics[index];
          const levelDiff = newLevel - currentItem.level;

          currentItem.parentId = this.newParentId || null;
          currentItem.level = newLevel;

          updateChildLevels(subjectId, levelDiff);
        }
      });

      this.localFormData.topics = newTopics;
      this.forceUpdate++;

      const parentName = newParent
        ? newParent.displayName || newParent.name
        : "Root Level";
      this.showNotification(
        `Moved ${this.selectedForFilter.length} competencies to ${parentName}`,
        "success"
      );

      this.selectedForFilter = [];
      this.newParentId = "";
    },

    onDragStart(event, subject, index) {
      this.draggedItem = { ...subject, index };
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/html", event.target.outerHTML);
    },

    onDragOver(event, subject) {
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";

      if (this.draggedItem && this.draggedItem.id !== subject.id) {
        this.dropTarget = subject;
      }
    },

    onDragLeave() {
      this.dropTarget = null;
    },

    onDrop(event, targetSubject) {
      event.preventDefault();

      if (!this.draggedItem || this.draggedItem.id === targetSubject.id) {
        this.dropTarget = null;
        return;
      }

      if (this.isChildOf(targetSubject.id, this.draggedItem.id)) {
        this.showNotification(
          "Cannot move a parent under its own child",
          "error"
        );
        this.dropTarget = null;
        return;
      }

      const newTopics = [...this.localFormData.topics];
      const draggedIndex = newTopics.findIndex(
        (s) => s.id === this.draggedItem.id
      );
      const targetLevel = targetSubject.level + 1;

      if (draggedIndex > -1) {
        const draggedItem = newTopics[draggedIndex];
        const levelDifference = targetLevel - draggedItem.level;

        // Update dragged item itself
        draggedItem.parentId = targetSubject.id;
        draggedItem.level = targetLevel;

        // Update recursively all children of dragged item
        const updateChildrenLevels = (parentId, levelDiff) => {
          newTopics.forEach((item) => {
            if (item.parentId === parentId) {
              item.level += levelDiff;
              updateChildrenLevels(item.id, levelDiff);
            }
          });
        };

        updateChildrenLevels(draggedItem.id, levelDifference);
      }

      this.localFormData.topics = newTopics;
      this.forceUpdate++;
      this.dropTarget = null;

      this.showNotification(
        `Moved "${this.draggedItem.displayName || this.draggedItem.name
        }" under "${targetSubject.displayName || targetSubject.name}"`,
        "success"
      );
    },

    onDragEnd() {
      this.draggedItem = null;
      this.dropTarget = null;
    },

    // Enhanced edit modal methods
    getProjectedTotalWeight() {
      if (!this.editForm.weight) return this.getRelevantTotalWeight();

      const parentId = this.editingSubject?.parentId;
      const siblings = this.localFormData.topics.filter(
        (comp) => comp.parentId === parentId
      );
      let currentTotal = siblings.reduce(
        (sum, comp) => sum + (comp.weight || 0),
        0
      );

      if (this.editingIndex !== null) {
        const editingComp = this.localFormData.topics[this.editingIndex];
        if (editingComp.parentId === parentId) {
          currentTotal -= editingComp.weight;
        }
      }

      return currentTotal + this.editForm.weight;
    },

    // Hierarchical weight calculation methods
    getChildrenTotalWeight(parentId) {
      return this.localFormData.topics
        .filter((comp) => comp.parentId === parentId)
        .reduce((sum, comp) => sum + (comp.weight || 0), 0);
    },

    getParentWeightStatusClass(parentId) {
      const total = this.getChildrenTotalWeight(parentId);
      if (total === 100) return "parent-weight-perfect";
      if (total > 100) return "parent-weight-over";
      return "parent-weight-under";
    },

    isWeightInvalid(subject) {
      const siblings = this.localFormData.topics.filter(
        (comp) => comp.parentId === subject.parentId
      );
      const total = siblings.reduce((sum, comp) => sum + (comp.weight || 0), 0);
      return total !== 100;
    },

    // Basic methods
    getParentName(parentId) {
      if (!parentId) return null;
      const parent = this.localFormData.topics.find(
        (comp) => comp.id === parentId
      );
      return parent ? parent.displayName || parent.name : null;
    },

    getChildrenCount(parentId) {
      return this.localFormData.topics.filter(
        (comp) => comp.parentId === parentId
      ).length;
    },

    getWeightStatusClass() {
      return this.canProceed ? "weight-perfect" : "weight-incomplete";
    },

    getWeightBadgeClass(weight) {
      if (weight >= 50) return "weight-high";
      if (weight >= 25) return "weight-medium";
      return "weight-low";
    },

    // Hierarchical sorting method
    sortHierarchically(subjects) {
      const sorted = [];
      const processed = new Set();

      const addSubjectAndChildren = (parentId) => {
        const children = subjects
          .filter(
            (comp) => comp.parentId === parentId && !processed.has(comp.id)
          )
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

        children.forEach((child) => {
          if (!processed.has(child.id)) {
            sorted.push(child);
            processed.add(child.id);
            addSubjectAndChildren(child.id);
          }
        });
      };

      addSubjectAndChildren(null);
      return sorted;
    },

    getRelevantTotalWeight() {
      const parentId = this.editingSubject?.parentId;
      const siblings = this.localFormData.topics.filter(
        (comp) => comp.parentId === parentId
      );
      return siblings.reduce((sum, comp) => sum + (comp.weight || 0), 0);
    },

    isChildOf(childId, parentId) {
      const child = this.localFormData.topics.find(
        (comp) => comp.id === childId
      );
      if (!child || !child.parentId) return false;

      if (child.parentId === parentId) return true;
      return this.isChildOf(child.parentId, parentId);
    },

    // Modal methods
    openAddSubjectModal() {
      this.showAddSubjectModal = true;
      this.selectedSubjects = [];
      this.subjectSearchQuery = "";
      this.currentPage = 1;
    },

    closeAddSubjectModal() {
      this.showAddSubjectModal = false;
      this.selectedSubjects = [];
      this.subjectSearchQuery = "";
      this.pendingParent = null;
    },

    openCreateNewModal() {
      this.showCreateNewModal = true;
      this.createNewForm = {
        name: "",
        description: "",
      };
    },

    closeCreateNewModal() {
      this.showCreateNewModal = false;
      this.createNewForm = {
        name: "",
        description: "",
        descriptorStatus: false,
      };
    },

    async createNewSubject() {
      if (!this.canCreateNew) return;

      const payload = { competencyName: this.createNewForm.name.trim(), competencyType: 1, description: this.createNewForm?.description || "" }

      const newComp = await this.CreateNewCompetency(payload)
      if (newComp) {
        const newSubject = {
          id: newComp.competencyId,
          name: this.createNewForm.name.trim(),
          description: this.createNewForm?.description || "",
        };

        this.databaseSubjects.unshift(newSubject);
        this.closeCreateNewModal();
        this.showNotification(
          "New Competency created and added to the list!",
          "success"
        );
      } else {
        this.showNotification(
          "New Competency not created.",
          "error"
        );
      }
    },

    isSelected(subjectId) {
      return this.selectedSubjects.includes(subjectId);
    },

    toggleSelection(subjectId) {
      const index = this.selectedSubjects.indexOf(subjectId);
      if (index > -1) {
        this.selectedSubjects.splice(index, 1);
      } else {
        this.selectedSubjects.push(subjectId);
      }
    },

    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedSubjects = this.selectedSubjects.filter(
          (id) => !this.paginatedSubjects.some((comp) => comp.id === id)
        );
      } else {
        this.paginatedSubjects.forEach((comp) => {
          if (!this.isSelected(comp.id)) {
            this.selectedSubjects.push(comp.id);
          }
        });
      }
    },

    async previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.databaseSubjects = await this.getCompetencyData();
      }
    },

    async nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.databaseSubjects = [];
        const data = await this.getCompetencyData();
        this.databaseSubjects = data;
      }
    },

    generateWithAI() {
      this.showNotification("AI generation feature coming soon!", "info");
    },

    addSelectedSubjects() {
      if (this.selectedSubjects.length === 0) return;

      const selectedDbSubjects = this.databaseSubjects.filter((comp) =>
        this.selectedSubjects.includes(comp.id)
      );

      const newTopics = [...this.localFormData.topics];
      let equalWeights = (100 / selectedDbSubjects.length).toFixed(0)
      selectedDbSubjects.forEach((dbComp, index) => {
        let descriptorStatus = dbComp.descriptorStatus || false;

        // Check if pending parent or any ancestor has descriptor status
        if (this.pendingParent) {
          if (
            this.pendingParent.descriptorStatus ||
            this.hasAncestorWithDescriptor(this.pendingParent.id)
          ) {
            descriptorStatus = false; // Force disable if parent/ancestor has descriptor
          }
        }

        if (index === selectedDbSubjects.length - 1) {
          if (selectedDbSubjects.length > 1) {
            equalWeights = 100 - (equalWeights * (selectedDbSubjects.length - 1))
          }
        }
        const newSubject = {
          id: dbComp?.id,
          subjectId: dbComp?.id,
          name: dbComp.name,
          displayName: dbComp?.displayName || "",
          description: dbComp?.description || "",
          parentId: this.pendingParent ? this.pendingParent.id : null,
          level: this.pendingParent ? this.pendingParent.level + 1 : 1,
          weight: parseInt(equalWeights),
          descriptorStatus: descriptorStatus,
        };
        newTopics.push(newSubject);
      });

      this.localFormData = {
        ...this.localFormData,
        topics: newTopics,
      };

      this.forceUpdate++;
      this.closeAddSubjectModal();

      const parentInfo = this.pendingParent
        ? ` as children of "${this.pendingParent.displayName || this.pendingParent.name
        }" (Level ${this.pendingParent.level + 1})`
        : " as Level 1 competencies";
      this.showNotification(
        `${selectedDbSubjects.length} competencies added${parentInfo}! Please use the Edit button to configure weights.`,
        "success"
      );
    },

    // CRUD Operations
    addChildSubject(parent) {
      this.showAddSubjectModal = true;
      this.selectedSubjects = [];
      this.subjectSearchQuery = "";
      this.currentPage = 1;
      this.pendingParent = parent;

      this.showNotification(
        `Adding child competency to "${parent.displayName || parent.name
        }" (Level ${parent.level + 1})`,
        "info"
      );
    },

    editSubject(index) {
      const subject = this.filteredSubjects[index];
      const actualIndex = this.localFormData.topics.findIndex(
        (comp) => comp.id === subject.id
      );

      this.editingIndex = actualIndex;
      this.editingSubject = this.localFormData.topics[actualIndex];

      this.editForm = {
        displayName:
          this.editingSubject.displayName || this.editingSubject.name,
        weight: this.editingSubject.weight,
        description: this.editingSubject.description || "",
        descriptorStatus: this.editingSubject.descriptorStatus || false,
      };
      this.showEditModal = true;
    },

    closeEditModal() {
      this.showEditModal = false;
      this.editingIndex = null;
      this.editingSubject = null;
      this.editForm = {
        displayName: "",
        weight: null,
        description: "",
        descriptorStatus: false,
      };
    },

    saveEditedSubject() {
      if (!this.canSaveEdit) {
        this.showNotification(
          "Please fill all required fields correctly",
          "error"
        );
        return;
      }

      // Store the old descriptor status for comparison
      const oldDescriptorStatus = this.editingSubject.descriptorStatus;
      let descriptorStatus = this.editForm.descriptorStatus;

      // If trying to enable descriptor when ancestor has it, force disable
      if (
        descriptorStatus &&
        this.hasAncestorWithDescriptor(this.editingSubject.id)
      ) {
        descriptorStatus = false;
        this.showNotification(
          "Cannot enable descriptor: An ancestor competency already has descriptor enabled",
          "warning"
        );
      }

      // If trying to enable descriptor when descendant has it, force disable
      if (
        descriptorStatus &&
        this.hasDescendantWithDescriptor(this.editingSubject.id)
      ) {
        descriptorStatus = false;
        this.showNotification(
          "Cannot enable descriptor: A descendant competency already has descriptor enabled",
          "warning"
        );
      }

      const updatedSubject = {
        ...this.editingSubject,
        displayName: this.editForm.displayName.trim(),
        weight: this.editForm.weight,
        description: this.editForm.description.trim(),
        descriptorStatus: descriptorStatus,
      };

      const newTopics = [...this.localFormData.topics];
      newTopics.splice(this.editingIndex, 1, updatedSubject);

      this.localFormData = {
        ...this.localFormData,
        topics: newTopics,
      };

      // Update hierarchical descriptor statuses if descriptor status changed
      if (descriptorStatus !== oldDescriptorStatus) {
        this.updateHierarchicalDescriptorStatuses(
          this.editingSubject.id,
          descriptorStatus
        );
      }

      this.forceUpdate++;
      this.closeEditModal();
      this.showNotification("Competency updated successfully", "success");
    },

    deleteComp(subject) {
      const actualIndex = this.localFormData.topics.findIndex(
        (comp) => comp.id === subject.id
      );
      const comp = this.localFormData.topics[actualIndex];

      const hasChildren = this.localFormData.topics.some(
        (c) => c.parentId === comp.id
      );

      if (hasChildren) {
        if (!this.removeItemsFilter) {
          if (
            !confirm(
              "This Competency has children. Deleting it will also delete all its children. Continue?"
            )
          ) {
            return;
          }
        }
        this.deleteSubjectAndChildren(comp.id);
        if (!this.removeItemsFilter) {
          this.showNotification("Competency and its children deleted", "warning");
        }
      } else {
        if (this.removeItemsFilter || confirm("Are you sure you want to delete this Competency?")) {
          const newTopics = [...this.localFormData.topics];
          newTopics.splice(actualIndex, 1);

          this.localFormData = {
            ...this.localFormData,
            topics: newTopics,
          };

          this.forceUpdate++;
          if (!this.removeItemsFilter) {
            this.showNotification("Competency deleted successfully", "success");
          }
        }
      }
    },

    deleteSubject(index) {
      const subject = this.filteredSubjects[index];
      this.deleteComp(subject)
    },

    deleteSubjectAndChildren(subjectId) {
      const children = this.localFormData.topics.filter(
        (comp) => comp.parentId === subjectId
      );
      children.forEach((child) => {
        this.deleteSubjectAndChildren(child.id);
      });

      const newTopics = this.localFormData.topics.filter(
        (comp) => comp.id !== subjectId
      );
      this.localFormData = {
        ...this.localFormData,
        topics: newTopics,
      };
      this.forceUpdate++;
    },

    configureSubject() {
      if (!this.canProceed) {
        this.showNotification(
          "All weight requirements must be met before proceeding",
          "error"
        );
        return;
      }

      this.showNotification(
        "Competency Management completed successfully!",
        "success"
      );
      this.$emit("step-completed", 2);
    },

    showNotification(message, type = "info") {
      const alertClass =
        {
          success: "alert-success",
          error: "alert-danger",
          info: "alert-info",
          warning: "alert-warning",
        }[type] || "alert-info";

      const notification = document.createElement("div");
      notification.className = `alert ${alertClass} alert-dismissible fade show position-fixed`;
      notification.style.cssText =
        "top: 20px; right: 20px; z-index: 9999; min-width: 300px;";
      notification.innerHTML = `
        ${message}
        <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
      `;

      document.body.appendChild(notification);

      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 5000);
    },

    // Check if any ancestor (parent, grandparent, etc.) has descriptor status
    hasAncestorWithDescriptor(subjectId) {
      const subject = this.localFormData.topics.find(
        (topic) => topic.id === subjectId
      );
      if (!subject || !subject.parentId) return false;

      const parent = this.localFormData.topics.find(
        (topic) => topic.id === subject.parentId
      );
      if (!parent) return false;

      if (parent.descriptorStatus) return true;

      // Recursively check parent's ancestors
      return this.hasAncestorWithDescriptor(parent.id);
    },

    // Check if any descendant (child, grandchild, etc.) has descriptor status
    hasDescendantWithDescriptor(subjectId) {
      const directChildren = this.localFormData.topics.filter(
        (topic) => topic.parentId === subjectId
      );

      for (const child of directChildren) {
        if (child.descriptorStatus) return true;

        // Recursively check child's descendants
        if (this.hasDescendantWithDescriptor(child.id)) return true;
      }

      return false;
    },

    // Get all descendants of a subject
    getAllDescendants(subjectId) {
      const descendants = [];
      const directChildren = this.localFormData.topics.filter(
        (topic) => topic.parentId === subjectId
      );

      directChildren.forEach((child) => {
        descendants.push(child);
        descendants.push(...this.getAllDescendants(child.id));
      });

      return descendants;
    },

    // Get all ancestors of a subject
    getAllAncestors(subjectId) {
      const ancestors = [];
      const subject = this.localFormData.topics.find(
        (topic) => topic.id === subjectId
      );

      if (subject && subject.parentId) {
        const parent = this.localFormData.topics.find(
          (topic) => topic.id === subject.parentId
        );
        if (parent) {
          ancestors.push(parent);
          ancestors.push(...this.getAllAncestors(parent.id));
        }
      }

      return ancestors;
    },

    // Update descriptor statuses throughout the hierarchy
    updateHierarchicalDescriptorStatuses(
      changedSubjectId,
      newDescriptorStatus
    ) {
      const newTopics = [...this.localFormData.topics];

      if (newDescriptorStatus) {
        // If enabling descriptor status, disable it for all ancestors and descendants
        const ancestors = this.getAllAncestors(changedSubjectId);
        const descendants = this.getAllDescendants(changedSubjectId);

        // Disable for all ancestors
        ancestors.forEach((ancestor) => {
          const index = newTopics.findIndex(
            (topic) => topic.id === ancestor.id
          );
          if (index > -1) {
            newTopics[index] = { ...newTopics[index], descriptorStatus: false };
          }
        });

        // Disable for all descendants
        descendants.forEach((descendant) => {
          const index = newTopics.findIndex(
            (topic) => topic.id === descendant.id
          );
          if (index > -1) {
            newTopics[index] = { ...newTopics[index], descriptorStatus: false };
          }
        });
      }

      this.localFormData.topics = newTopics;
    },

    // Get the reason why descriptor option is hidden
    getDescriptorHiddenReason() {
      if (!this.editingSubject) return null;

      // Check ancestors
      const ancestorWithDescriptor = this.getAncestorWithDescriptor(
        this.editingSubject.id
      );
      if (ancestorWithDescriptor) {
        return {
          type: "ancestor",
          subject: ancestorWithDescriptor,
        };
      }

      // Check descendants
      const descendantWithDescriptor = this.getDescendantWithDescriptor(
        this.editingSubject.id
      );
      if (descendantWithDescriptor) {
        return {
          type: "descendant",
          subject: descendantWithDescriptor,
        };
      }

      return null;
    },

    // Get specific ancestor with descriptor
    getAncestorWithDescriptor(subjectId) {
      const subject = this.localFormData.topics.find(
        (topic) => topic.id === subjectId
      );
      if (!subject || !subject.parentId) return null;

      const parent = this.localFormData.topics.find(
        (topic) => topic.id === subject.parentId
      );
      if (!parent) return null;

      if (parent.descriptorStatus) return parent;

      return this.getAncestorWithDescriptor(parent.id);
    },

    // Get specific descendant with descriptor
    getDescendantWithDescriptor(subjectId) {
      const directChildren = this.localFormData.topics.filter(
        (topic) => topic.parentId === subjectId
      );

      for (const child of directChildren) {
        if (child.descriptorStatus) return child;

        const descendant = this.getDescendantWithDescriptor(child.id);
        if (descendant) return descendant;
      }

      return null;
    },
  },

  async mounted() {
    this.databaseSubjects = await this.getCompetencyData();
  },

  beforeUnmount() {
    if (this.checkIntervalId) clearInterval(this.checkIntervalId);
  },

};
</script>

<style scoped>
.topics-management {
  min-height: 100vh;
  background-color: #f8f9fa;
  padding: 2rem;
}

.header-section {
  margin-bottom: 2rem;
}

.page-title {
  font-size: 2rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.page-subtitle {
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
}

.main-content {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.subject-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.subject-count {
  font-size: 0.875rem;
  color: #6b7280;
  background: #f3f4f6;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 1rem;
}

/* Filter Panel Styles */
.filter-actions-container {
  position: relative;
}

.filter-btn {
  background: #f3f4f6;
  border: 2px solid #e5e7eb;
  color: #374151;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
}

.filter-btn:hover,
.filter-btn.active {
  background: #8b5cf6;
  border-color: #8b5cf6;
  color: white;
}

.filter-count {
  background: #ef4444;
  color: white;
  font-size: 0.75rem;
  padding: 0.125rem 0.375rem;
  border-radius: 10px;
  min-width: 1.25rem;
  text-align: center;
}

.action-dropdown {
  position: absolute;
  top: calc(100% + 0.5rem);
  /* places dropdown just below the button */
  left: 0;
  /* aligns left edge of dropdown with the button */
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
  z-index: 10;
  width: 160px;
  /* padding: 0.25rem 0; */
}


.action-dropdown ul {
  list-style: none;
  margin: 0;
  padding: 0;
}

.action-dropdown li {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
  /* border-radius: 8px; */
}

.action-dropdown li:first-child {
  border-top-right-radius: 8px;
  border-top-left-radius: 8px;
}


.action-dropdown li:last-child {
  border-bottom: none;
  border-bottom-right-radius: 8px;
  border-end-start-radius: 8px;
}


.action-dropdown li:hover {
  background-color: #f8f4ff;
  color: #111827;
  border: 1px solid #e5e7eb;
  border-left: 0px;
  border-right: 0px;
}

.action-dropdown-icon {
  background-color: #f8f4ff;
  padding: 10px 12px;
  border-radius: 100%;
}

.action-dropdown li:hover .action-dropdown-icon {
  background-color: #ffffff;
  padding: 10px 12px;
  border-radius: 100%;
}



.filter-panel {
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  overflow: hidden;
}

.filter-header {
  background: #475569;
  color: white;
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.filter-header h3 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
}

.close-filter-btn {
  background: none;
  border: none;
  color: white;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.close-filter-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.filter-content {
  padding: 1.5rem;
}

.filter-section {
  margin-bottom: 2rem;
}

.filter-section h4 {
  color: #374151;
  font-weight: 600;
  margin-bottom: 1rem;
  font-size: 1rem;
}

.selected-subjects-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  min-height: 2.5rem;
  align-items: flex-start;
}

.selected-subject-item {
  background: #8b5cf6;
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
}

.remove-btn {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  transition: background-color 0.2s ease;
}

.remove-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.no-selection {
  color: #6b7280;
  font-style: italic;
  padding: 1rem;
  text-align: center;
  background: #f9fafb;
  border-radius: 6px;
  border: 2px dashed #d1d5db;
}

.filter-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}

.action-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.action-group label {
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
}

.weight-input-group,
.parent-select-group {
  display: flex;
  gap: 0.75rem;
}

.weight-input,
.parent-select {
  flex: 1;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  outline: none;
}

.weight-input:focus,
.parent-select:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.apply-btn {
  background: #10b981;
  color: white;
  border: none;
  padding: 0.75rem 1.25rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
  white-space: nowrap;
}

.apply-btn:hover:not(:disabled) {
  background: #059669;
}

.apply-btn:disabled {
  background: #d1d5db;
  color: #9ca3af;
  cursor: not-allowed;
}

/* Drag and Drop Styles */
.drag-handle {
  cursor: grab;
  color: #9ca3af;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.drag-handle:hover {
  background: #f3f4f6;
  color: #6b7280;
}

.table-row.dragging {
  opacity: 0.5;
  background: #f0f9ff;
}

.table-row.drop-target {
  background: #ecfdf5;
  border: 2px dashed #10b981;
}

.drag-handle:active {
  cursor: grabbing;
}

/* Checkbox Column */
.checkbox-col {
  width: 40px;
  text-align: center;
}

.table-checkbox {
  width: 1rem;
  height: 1rem;
  accent-color: #8b5cf6;
  cursor: pointer;
}

.search-container {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
}

.search-input {
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  width: 250px;
  outline: none;
}

.search-input:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.create-btn {
  background-color: #8b5cf6;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.create-btn:hover {
  background-color: #7c3aed;
}

/* Weight Alert */
.weight-alert {
  background-color: #fef3cd;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  color: #92400e;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.weight-alert i {
  font-size: 1.25rem;
  color: #d97706;
  margin-top: 0.125rem;
}

.weight-error-item {
  margin-bottom: 0.25rem;
}

.weight-error-item:last-child {
  margin-bottom: 0;
}

/* Table Styles */
.table-container {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 1rem;
}

.subject-table {
  width: 100%;
  border-collapse: collapse;
}

.subject-table th {
  background-color: #f9fafb;
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
  border-bottom: 1px solid #e5e7eb;
}

.subject-table td {
  padding: 1rem;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
}

.table-row {
  transition: all 0.2s ease;
}

.table-row:hover {
  background-color: #f9fafb;
}

.table-row.weight-invalid {
  background-color: #fef2f2;
}

.subject-col {
  width: 380px;
}

.parent-col {
  width: 200px;
}

.level-col {
  width: 80px;
}

.weight-col {
  width: 120px;
}

.descriptor-col {
  width: 150px;
}

.actions-col {
  width: 180px;
}

.subject-name-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  position: relative;
  min-height: 24px;
  padding-left: 0;
}

/* Tree Structure Visualization */
.tree-structure {
  position: relative;
  margin-right: 0.5rem;
}

.tree-vertical-line {
  position: absolute;
  left: -20px;
  top: -12px;
  width: 2px;
  height: 24px;
  background-color: #cbd5e1;
  border-radius: 1px;
}

.tree-horizontal-line {
  position: absolute;
  left: -20px;
  top: 10px;
  width: 18px;
  height: 2px;
  background-color: #cbd5e1;
  border-radius: 1px;
}

/* Adjust indentation based on level */
.table-row[data-level="2"] .subject-name-container {
  padding-left: 30px;
}

.table-row[data-level="3"] .subject-name-container {
  padding-left: 60px;
}

.table-row[data-level="4"] .subject-name-container {
  padding-left: 90px;
}

.table-row[data-level="5"] .subject-name-container {
  padding-left: 120px;
}

.table-row[data-level="6"] .subject-name-container {
  padding-left: 150px;
}

.level-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 0.75rem;
}

.level-1-icon {
  background-color: #8b5cf6;
  color: white;
}

.level-2-icon {
  background-color: #3b82f6;
  color: white;
}

.level-3-icon {
  background-color: #06d6a0;
  color: white;
}

.level-4-icon {
  background-color: #f59e0b;
  color: white;
}

.level-5-icon {
  background-color: #ef4444;
  color: white;
}

.level-6-icon {
  background-color: #6b7280;
  color: white;
}

.subject-name {
  font-weight: 600;
  font-size: 0.95rem;
}

.level-1-text {
  color: #8b5cf6;
  font-weight: 700;
  font-size: 1rem;
}

.level-2-text {
  color: #1f2937;
  font-weight: 600;
}

.level-3-text {
  color: #6b7280;
  font-weight: 500;
}

.level-4-text {
  color: #92400e;
  font-weight: 500;
}

.level-5-text {
  color: #dc2626;
  font-weight: 500;
}

.level-6-text {
  color: #4b5563;
  font-weight: 500;
}

.children-count {
  color: black;
  font-size: 0.75rem;
  font-weight: 400;
  margin-left: 0.5rem;
}

.parent-weight-status {
  margin-left: 0.5rem;
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.parent-weight-perfect {
  background-color: #dcfce7;
  color: #166534;
}

.parent-weight-under {
  background-color: #fef3cd;
  color: #92400e;
}

.parent-weight-over {
  background-color: #fecaca;
  color: #dc2626;
}

.parent-text {
  color: black;
  font-weight: 400;
  font-size: 0.875rem;
}

.level-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  font-weight: 400;
  font-size: 0.875rem;
  color: black;
}

.weight-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
}

.weight-high {
  background-color: #dc2626;
  color: white;
}

.weight-medium {
  background-color: #f59e0b;
  color: white;
}

.weight-low {
  background-color: #10b981;
  color: white;
}

.actions-container {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.action-btn {
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease;
  font-size: 0.875rem;
}

.edit-btn {
  color: #6b7280;
}

.edit-btn:hover {
  background-color: #f3f4f6;
}

.delete-btn {
  color: #ef4444;
}

.delete-btn:hover {
  background-color: #fef2f2;
}

.add-child-btn {
  color: #10b981;
}

.add-child-btn:hover {
  background-color: #ecfdf5;
}

.table-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.weight-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
}

.weight-perfect {
  color: #10b981;
}

.weight-incomplete {
  color: #f59e0b;
}

.configure-section {
  text-align: right;
}

.configure-btn {
  background-color: #8b5cf6;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.configure-btn:hover:not(:disabled) {
  background-color: #7c3aed;
}

.configure-btn:disabled {
  background-color: #d1d5db;
  color: #9ca3af;
  cursor: not-allowed;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.add-modal-content {
  background: white;
  border-radius: 12px;
  width: 95%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
}

.edit-header-content {
  flex: 1;
}

.simple-info {
  margin-top: 0.75rem;
}

.simple-info-line {
  margin-bottom: 0.5rem;
}

.simple-info-line:last-child {
  margin-bottom: 0;
}

.simple-label {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}

.simple-value {
  font-size: 0.875rem;
  color: #6b7280;
  font-family: "Courier New", monospace;
  margin-left: 0.25rem;
  margin-right: 0.5rem;
}

.simple-separator {
  color: #d1d5db;
  margin: 0 0.5rem;
}

.simple-name {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}

.modal-body {
  padding: 1.5rem;
  flex: 1;
  overflow-y: auto;
}

/* Modern Edit Modal Styles

.form-group:last-child {
  border-bottom: none;
}

.weight-input {
  padding-right: 2.5rem;
}

.field-error {
  color: #dc2626;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
}

.footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
} */

/* Standard Modal Header */

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  padding: 0.5rem;
  border-radius: 4px;
}

.close-btn:hover {
  background-color: #f3f4f6;
}

.modal-body {
  padding: 1.5rem;
  flex: 1;
  overflow-y: auto;
}

/* Form Styles */
.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.modal-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  outline: none;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.modal-input:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.modal-textarea {
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
}

.disabled-input {
  background-color: #f9fafb;
  color: #6b7280;
  cursor: not-allowed;
}

/* Search Section */
.search-section {
  margin-bottom: 1.5rem;
}

.search-container-full {
  position: relative;
  width: 100%;
}

.search-input-full {
  width: 100%;
  padding: 0.875rem 1rem 0.875rem 2.5rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  outline: none;
  box-sizing: border-box;
}

.search-input-full:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
}

.create-subject-btn,
.ai-generate-btn {
  flex: 1;
  padding: 0.875rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: none;
}

.create-subject-btn {
  background-color: #8b5cf6;
  color: white;
}

.create-subject-btn:hover {
  background-color: #7c3aed;
}

.ai-generate-btn {
  background-color: #f59e0b;
  color: white;
}

.ai-generate-btn:hover {
  background-color: #d97706;
}

/* Subject Cards Grid */
.subject-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 0.5rem;
  margin-bottom: 2rem;
}

.subject-card {
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.subject-card:hover {
  border-color: #8b5cf6;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.1);
}

.subject-card.selected {
  border-color: #8b5cf6;
  background-color: #f8f4ff;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.2);
}

.card-content {
  text-align: center;
  width: 100%;
}

.card-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: #1f2937;
  line-height: 1.3;
}

.subject-card.selected .card-name {
  color: #8b5cf6;
  font-weight: 600;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #6b7280;
}

.empty-icon {
  width: 64px;
  height: 64px;
  border: 2px dashed #d1d5db;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 1.5rem;
  color: #d1d5db;
}

.empty-state h4 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

/* Pagination */
.pagination-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.pagination-info {
  font-size: 0.875rem;
  color: #6b7280;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.pagination-btn {
  background: none;
  border: 1px solid #d1d5db;
  color: #374151;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.875rem;
}

.pagination-btn:hover:not(:disabled) {
  background-color: #f3f4f6;
  border-color: #9ca3af;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-indicator {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}

/* Modal Footer */
.modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
  background-color: #f9fafb;
  flex-shrink: 0;
}

.footer-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.footer-right {
  display: flex;
  gap: 1rem;
}

.select-all-btn {
  background: none;
  border: 1px solid #d1d5db;
  color: #374151;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.select-all-btn:hover {
  background-color: #f3f4f6;
}

.selection-count {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}

.cancel-btn {
  background: none;
  border: 1px solid #d1d5db;
  color: #374151;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
}

.cancel-btn:hover {
  background-color: #f3f4f6;
}

.add-selected-btn {
  background-color: #8b5cf6;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
}

.add-selected-btn:hover:not(:disabled) {
  background-color: #7c3aed;
}

.add-selected-btn:disabled {
  background-color: #d1d5db;
  color: #9ca3af;
  cursor: not-allowed;
}

.save-btn {
  background-color: #8b5cf6;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
}

.save-btn:hover:not(:disabled) {
  background-color: #7c3aed;
}

.save-btn:disabled {
  background-color: #d1d5db;
  color: #9ca3af;
  cursor: not-allowed;
}

/* descriptor button style here */
/* Enhanced Descriptor Status Checkbox Styles - Single Row Layout */
.descripter-status-div {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.25rem;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.descripter-status-div:hover {
  border-color: #8b5cf6;
  background: #faf7ff;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.1);
}

.descripter-status-title {
  font-size: 0.95rem;
  font-weight: 500;
  color: #374151;
  line-height: 1.5;
  margin: 0;
  flex: 1;
}

.descripter-status-input {
  width: 20px !important;
  height: 20px !important;
  accent-color: #8b5cf6;
  cursor: pointer;
  border: 2px solid #d1d5db;
  border-radius: 4px;
  transition: all 0.3s ease;
  position: relative;
  margin: 0;
  flex-shrink: 0;
}

.descripter-status-input:checked {
  background-color: #8b5cf6;
  border-color: #8b5cf6;
  transform: scale(1.1);
}

.descripter-status-input:hover {
  border-color: #8b5cf6;
  transform: scale(1.05);
}

.descripter-status-input:focus-visible {
  outline: 2px solid #8b5cf6;
  outline-offset: 2px;
}

/* Remove the previous bottom margin and border-top styles */
.descripter-status-div span:last-child {
  display: flex;
  align-items: center;
  margin: 0;
  padding: 0;
  border-top: none;
}

/* Add a subtle animation when checked */
.descripter-status-input:checked {
  animation: checkboxPulse 0.3s ease-in-out;
}

@keyframes checkboxPulse {
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.15);
  }

  100% {
    transform: scale(1.1);
  }
}

/* Custom checkmark styling (for better cross-browser support) */
.descripter-status-input:checked::before {
  content: "✓";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 12px;
  font-weight: bold;
  line-height: 1;
}

/* Responsive */
@media (max-width: 768px) {
  .topics-management {
    padding: 1rem;
  }

  .main-content {
    padding: 1rem;
  }

  .subject-header {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .header-right {
    flex-direction: column;
    gap: 1rem;
  }

  .search-input {
    width: 100%;
  }

  .filter-actions {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .action-buttons {
    flex-direction: column;
  }

  .subject-cards-grid {
    grid-template-columns: 1fr;
  }

  .pagination-section {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }

  .modal-footer {
    flex-direction: column;
    gap: 1rem;
  }

  .footer-left,
  .footer-right {
    width: 100%;
    justify-content: center;
  }

  .add-modal-content {
    width: 95%;
    margin: 1rem;
  }

  .table-container {
    overflow-x: auto;
  }

  .subject-table {
    min-width: 700px;
  }

  .tree-structure {
    display: none;
  }

  .table-row[data-level="2"] .subject-name-container,
  .table-row[data-level="3"] .subject-name-container,
  .table-row[data-level="4"] .subject-name-container,
  .table-row[data-level="5"] .subject-name-container,
  .table-row[data-level="6"] .subject-name-container {
    padding-left: 0;
  }

  .descripter-status-div {
    padding: 1rem;
    gap: 0.75rem;
  }

  .descripter-status-title {
    font-size: 0.875rem;
  }

  .descripter-status-input {
    width: 18px !important;
    height: 18px !important;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 1.5rem;
  }

  .section-title {
    font-size: 1rem;
  }

  .header-left {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .subject-col {
    width: 250px;
  }

  .actions-col {
    width: 140px;
  }

  .action-btn {
    padding: 0.375rem;
  }

  .configure-btn {
    width: 100%;
    text-align: center;
  }

  .subject-cards-grid {
    gap: 0.5rem;
  }

  .subject-card {
    padding: 0.375rem 0.5rem;
    min-height: 40px;
  }

  .card-name {
    font-size: 0.8rem;
  }

  .descripter-status-div {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .descripter-status-div span:last-child {
    align-self: flex-end;
  }
}

/* Animation for card selection */
@keyframes cardSelect {
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.02);
  }

  100% {
    transform: scale(1);
  }
}

.subject-card.selected {
  animation: cardSelect 0.3s ease-in-out;
}

/* Drag and drop animations */
@keyframes dragEnter {
  0% {
    transform: scale(1);
  }

  100% {
    transform: scale(1.02);
  }
}

.table-row.drop-target {
  animation: dragEnter 0.2s ease-in-out;
}

/* Focus states for accessibility */
.subject-card:focus {
  outline: 2px solid #8b5cf6;
  outline-offset: 2px;
}

.pagination-btn:focus,
.action-btn:focus,
.create-btn:focus,
.filter-btn:focus {
  outline: 2px solid #8b5cf6;
  outline-offset: 2px;
}

.modal-input:focus {
  outline: none;
}

/* Print styles */
@media print {

  .actions-container,
  .configure-section,
  .header-right,
  .filter-panel {
    display: none;
  }

  .table-row {
    cursor: default;
  }

  .main-content {
    box-shadow: none;
    border: 1px solid #e5e7eb;
  }

  .checkbox-col {
    display: none;
  }

  .drag-handle {
    display: none;
  }
}

/* Custom scrollbar for modals */
.modal-body::-webkit-scrollbar {
  width: 6px;
}

.modal-body::-webkit-scrollbar-track {
  background: #f1f5f9;
}

.modal-body::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Enhanced loading states */
.loading-state {
  opacity: 0.6;
  pointer-events: none;
  position: relative;
}

.loading-state::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 20px;
  height: 20px;
  margin: -10px 0 0 -10px;
  border: 2px solid #8b5cf6;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Enhanced hover effects */
.filter-btn,
.create-btn,
.apply-btn {
  position: relative;
  overflow: hidden;
}

.filter-btn::before,
.create-btn::before,
.apply-btn::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent);
  transition: left 0.5s;
}

.filter-btn:hover::before,
.create-btn:hover::before,
.apply-btn:hover:not(:disabled)::before {
  left: 100%;
}
</style>
